// Excel de Outbound Loads. Una fila por carga, con el mismo criterio de hueco
// que la pantalla: `cajas`/`pesoNeto`/`pesoBruto`/`palets` nulos dejan la celda
// VACÍA, nunca a cero — un cero en un Excel que alguien reenvía ya no se puede
// deshacer.
import ExcelJS from 'exceljs'
import type { TFunction } from 'i18next'
import type { CargaSalida, CargaSalidaLinea } from '@/types'
import { estadoPickingDe, modoTransporteDe, reservaDe } from './cargasSalida'

const HEADER = { bg: 'FF0284C7', font: 'FFFFFFFF' }   // sky-600
const BAND = 'FFF8FAFC'                                // slate-50
const BORDER = 'FFE2E8F0'                              // slate-200

function fill(argb: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } }
}

function borde(): ExcelJS.Borders {
  const lado = { style: 'thin' as const, color: { argb: BORDER } }
  return { top: lado, left: lado, bottom: lado, right: lado } as ExcelJS.Borders
}

function fecha(iso?: string | null): Date | null {
  if (!iso) return null
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

interface Opciones {
  isEmpresaAll: boolean
  t: TFunction
  generado?: Date
  /** Las líneas de las cargas, ya pedidas en bloque por el llamante. Opcional:
   *  sin ellas la hoja «Líneas» sale con su cabecera y vacía. */
  lineas?: CargaSalidaLinea[]
}

/** Construye el libro. Puro: no descarga nada, así se puede probar. */
export function buildCargasSalidaWorkbook(
  cargas: CargaSalida[],
  { isEmpresaAll, t, generado = new Date(), lineas }: Opciones,
): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'Airval Planning'
  wb.created = generado
  wb.title = String(t('cargasSalida.title'))

  const x = (k: string) => String(t(`cargasSalida.excel.${k}`))
  const estado = (c: CargaSalida) => String(t(
    c.expedida ? 'salesOrders.picking.expedida' : 'salesOrders.picking.enAlmacen',
  ))

  interface Col { titulo: string; ancho?: number; numFmt?: string; align?: 'right'
    valor: (c: CargaSalida) => string | number | Date | null }

  const cols: Col[] = [
    ...(isEmpresaAll ? [{ titulo: x('company'), ancho: 10, valor: (c: CargaSalida) => c.empresa } as Col] : []),
    { titulo: x('load'), ancho: 18, valor: c => c.carga },
    { titulo: x('customer'), ancho: 14, valor: c => c.cliente ?? null },
    { titulo: x('customerName'), ancho: 30, valor: c => c.clienteNombre?.trim() || null },
    { titulo: x('orders'), ancho: 22, valor: c => c.pedidos.join(', ') || null },
    { titulo: x('customerGroup'), ancho: 16, valor: c => c.grupoCliente ?? null },
    { titulo: x('incoterm'), ancho: 10, valor: c => c.incoterm ?? null },
    { titulo: x('warehouse'), ancho: 10, valor: c => c.almacen ?? null },
    { titulo: x('shipDate'), ancho: 14, numFmt: 'dd/mm/yyyy', valor: c => fecha(c.fechaEnvioPrevista) },
    // El modo YA RESUELTO, el mismo que pinta la tabla (`modoTransporteDe`):
    // si la carga está marcada como aérea dice AÉREO, y si no lo que diga
    // `modoTransporte`. `air` se queda al lado porque es la marca cruda, y
    // quien cuadre el fichero con D365 la necesita sin interpretar.
    {
      titulo: x('transportMode'), ancho: 12,
      valor: c => {
        const m = modoTransporteDe(c)
        return m ? String(t(`cargasSalida.tabla.modos.${m}`, { defaultValue: m })) : null
      },
    },
    { titulo: x('air'), ancho: 8, valor: c => (c.aerea ? String(t('salesOrders.picking.aerea')) : null) },
    { titulo: x('items'), ancho: 8, numFmt: '#,##0', align: 'right', valor: c => c.articulos },
    { titulo: x('lines'), ancho: 8, numFmt: '#,##0', align: 'right', valor: c => c.lineas },
    { titulo: x('boxes'), ancho: 8, numFmt: '#,##0', align: 'right', valor: c => c.cajas ?? null },
    { titulo: x('pallets'), ancho: 8, numFmt: '#,##0', align: 'right', valor: c => c.palets ?? null },
    // `pallets` es la cifra única (real, o mono si es estimada). Estas tres dejan
    // cuadrar a quien lo necesite: sobre qué base está, y los dos estimados.
    {
      titulo: x('palletsBasis'), ancho: 10,
      valor: c => (c.paletsSonReales == null
        ? null
        : String(t(`cargasSalida.tabla.palets.${c.paletsSonReales ? 'real' : 'mono'}`))),
    },
    { titulo: x('palletsMixed'), ancho: 10, numFmt: '#,##0', align: 'right', valor: c => c.paletsEstimadosMixto ?? null },
    { titulo: x('palletsMono'), ancho: 10, numFmt: '#,##0', align: 'right', valor: c => c.paletsEstimadosMono ?? null },
    { titulo: x('netWeight'), ancho: 12, numFmt: '#,##0.00', align: 'right', valor: c => c.pesoNeto ?? null },
    { titulo: x('grossWeight'), ancho: 12, numFmt: '#,##0.00', align: 'right', valor: c => c.pesoBruto ?? null },
    { titulo: x('qtyRequested'), ancho: 12, numFmt: '#,##0', align: 'right', valor: c => c.qtySolicitada },
    { titulo: x('qtyBooked'), ancho: 12, numFmt: '#,##0', align: 'right', valor: c => c.qtyReservada },
    { titulo: x('qtyPicked'), ancho: 12, numFmt: '#,##0', align: 'right', valor: c => c.qtyPicking },
    { titulo: x('qtyToPick'), ancho: 12, numFmt: '#,##0', align: 'right', valor: c => c.qtyPendiente },
    { titulo: x('bookingGap'), ancho: 12, numFmt: '#,##0', align: 'right', valor: c => reservaDe(c).falta },
    {
      titulo: x('pickingStatus'), ancho: 16,
      valor: c => String(t(`salesOrders.picking.estado.${estadoPickingDe(c)}`)),
    },
    { titulo: x('loadState'), ancho: 14, valor: estado },
    {
      titulo: x('palletised'), ancho: 12,
      valor: c => (c.paletizada == null
        ? null
        : String(t(c.paletizada ? 'salesOrders.picking.paletizada' : 'salesOrders.picking.noPaletizada'))),
    },
    { titulo: x('dock'), ancho: 12, valor: c => c.muelle ?? null },
  ]

  const ws = wb.addWorksheet(x('sheet').slice(0, 31), { views: [{ state: 'frozen', ySplit: 1 }] })
  ws.columns = cols.map(c => ({ width: c.ancho ?? 14 }))

  const cabecera = ws.addRow(cols.map(c => c.titulo))
  cabecera.height = 20
  cabecera.eachCell(celda => {
    celda.fill = fill(HEADER.bg)
    celda.font = { bold: true, color: { argb: HEADER.font }, size: 10 }
    celda.alignment = { vertical: 'middle', horizontal: 'left' }
    celda.border = borde()
  })

  cargas.forEach((c, i) => {
    const fila = ws.addRow(cols.map(col => col.valor(c) ?? null))
    fila.eachCell({ includeEmpty: true }, (celda, n) => {
      const col = cols[n - 1]
      if (!col) return
      celda.font = { size: 10 }
      celda.border = borde()
      if (col.numFmt) celda.numFmt = col.numFmt
      if (col.align === 'right') celda.alignment = { horizontal: 'right' }
      if (i % 2 === 1) celda.fill = fill(BAND)
    })
  })

  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } }

  anadirHojaLineas(wb, cargas, lineas, { isEmpresaAll, t })

  return wb
}

/**
 * Segunda hoja: UNA FILA POR LÍNEA de carga, con la cabecera repetida al lado.
 *
 * La primera hoja contesta «¿qué cargas salen?» y ésta «¿qué artículos van
 * dentro?», que hoy obliga a abrir la pantalla carga por carga. Las columnas de
 * la carga —cliente, almacén, fecha de salida— se REPITEN en cada línea: es lo
 * que permite ordenar por artículo, filtrar por cliente o montar una dinámica
 * de «cuánto de este artículo sale esta semana», que con una hoja por carga no
 * se puede.
 *
 * `lineas` llega ya pedida por el llamante (una sola llamada en bloque). Si no
 * llega —o viene vacía— la hoja se crea igualmente con su cabecera: una hoja
 * ausente parece que falte una función; una hoja vacía se lee como «no hay».
 *
 * El criterio de hueco es el de la otra hoja y el de la pantalla: `cajas`,
 * `pesoNeto` y `pesoBruto` nulos dejan la celda VACÍA, nunca a cero.
 */
function anadirHojaLineas(
  wb: ExcelJS.Workbook,
  cargas: CargaSalida[],
  lineas: CargaSalidaLinea[] | undefined,
  { isEmpresaAll, t }: Pick<Opciones, 'isEmpresaAll' | 't'>,
): void {
  const x = (k: string) => String(t(`cargasSalida.excel.${k}`))

  // Índice de cabeceras para no recorrer el array por cada línea.
  const porCarga = new Map(cargas.map(c => [c.carga, c]))

  interface ColL {
    titulo: string; ancho?: number; numFmt?: string; align?: 'right'
    valor: (l: CargaSalidaLinea, c?: CargaSalida) => string | number | Date | null
  }

  const cols: ColL[] = [
    ...(isEmpresaAll ? [{ titulo: x('company'), ancho: 10, valor: (l: CargaSalidaLinea) => l.empresa } as ColL] : []),
    { titulo: x('load'), ancho: 18, valor: l => l.carga },
    { titulo: x('customerName'), ancho: 30, valor: (_l, c) => c?.clienteNombre?.trim() || null },
    { titulo: x('warehouse'), ancho: 10, valor: (_l, c) => c?.almacen ?? null },
    { titulo: x('shipDate'), ancho: 14, numFmt: 'dd/mm/yyyy', valor: (_l, c) => fecha(c?.fechaEnvioPrevista) },
    { titulo: x('order'), ancho: 18, valor: l => l.numPedido ?? null },
    { titulo: x('article'), ancho: 18, valor: l => l.articulo },
    { titulo: x('articleName'), ancho: 34, valor: l => l.nombreMaterial?.trim() || null },
    { titulo: x('color'), ancho: 12, valor: l => l.color?.trim() || null },
    { titulo: x('style'), ancho: 12, valor: l => l.estilo?.trim() || null },
    { titulo: x('qtyRequested'), ancho: 12, numFmt: '#,##0', align: 'right', valor: l => l.qtySolicitada },
    { titulo: x('qtyBooked'), ancho: 12, numFmt: '#,##0', align: 'right', valor: l => l.qtyReservada },
    // La reserva contra entradas FUTURAS va en su propia columna y no sumada a
    // la física: mezclarlas diría «hay material» de algo que aún no ha llegado.
    { titulo: x('qtyBookedFuture'), ancho: 14, numFmt: '#,##0', align: 'right', valor: l => l.qtyReservadaPedido ?? null },
    { titulo: x('qtyPicked'), ancho: 12, numFmt: '#,##0', align: 'right', valor: l => l.qtyPicking },
    { titulo: x('qtyToPick'), ancho: 12, numFmt: '#,##0', align: 'right', valor: l => l.qtyPendiente },
    { titulo: x('boxes'), ancho: 8, numFmt: '#,##0', align: 'right', valor: l => l.cajas ?? null },
    { titulo: x('netWeight'), ancho: 12, numFmt: '#,##0.00', align: 'right', valor: l => l.pesoNeto ?? null },
    { titulo: x('grossWeight'), ancho: 12, numFmt: '#,##0.00', align: 'right', valor: l => l.pesoBruto ?? null },
    // Del ARTÍCULO, no de la línea: se repite entre cargas y NO se puede sumar.
    { titulo: x('stockAvailable'), ancho: 13, numFmt: '#,##0', align: 'right', valor: l => l.stockDisponible ?? null },
    { titulo: x('nextInbound'), ancho: 14, numFmt: 'dd/mm/yyyy', valor: l => fecha(l.proximaEntrada) },
  ]

  const ws = wb.addWorksheet(x('sheetLines').slice(0, 31), { views: [{ state: 'frozen', ySplit: 1 }] })
  ws.columns = cols.map(c => ({ width: c.ancho ?? 14 }))

  const cabecera = ws.addRow(cols.map(c => c.titulo))
  cabecera.height = 20
  cabecera.eachCell(celda => {
    celda.fill = fill(HEADER.bg)
    celda.font = { bold: true, color: { argb: HEADER.font }, size: 10 }
    celda.alignment = { vertical: 'middle', horizontal: 'left' }
    celda.border = borde()
  })

  ;(lineas ?? []).forEach((l, i) => {
    const c = porCarga.get(l.carga)
    const fila = ws.addRow(cols.map(col => col.valor(l, c) ?? null))
    fila.eachCell({ includeEmpty: true }, (celda, n) => {
      const col = cols[n - 1]
      if (!col) return
      celda.font = { size: 10 }
      celda.border = borde()
      if (col.numFmt) celda.numFmt = col.numFmt
      if (col.align === 'right') celda.alignment = { horizontal: 'right' }
      if (i % 2 === 1) celda.fill = fill(BAND)
    })
  })

  if (lineas?.length) {
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } }
  }
}

/** Construye y descarga el libro. */
export async function exportCargasSalidaExcel(
  cargas: CargaSalida[],
  opciones: Opciones,
): Promise<void> {
  const generado = opciones.generado ?? new Date()
  const wb = buildCargasSalidaWorkbook(cargas, { ...opciones, generado })
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${String(opciones.t('cargasSalida.excel.filename'))}_${
    generado.toISOString().slice(0, 10)}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}
